import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ArrowLeft, Loader2, Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
;

interface CustomInterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Question {
  id: string;
  question: string;
  type: string;
  difficulty: string;
}

interface GeneratedInterview {
  jobId: string;
  jobTitle: string;
  seniority: string;
  difficulty: string;
  interviewerGender: string;
  previewQuestions: boolean;
  questions: Question[];
  createdJobId?: string;
}

type Step = 1 | 2 | 'loading' | 3;

export default function CustomInterviewModal({ isOpen, onClose }: CustomInterviewModalProps) {
  const navigate = useNavigate();
  const { isRecruiterValue, organizationId } = useAuth();
  const [step, setStep] = useState<Step>(1);

  const [jobTitle, setJobTitle] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [seniority, setSeniority] = useState('Mid');
  const [interviewerGender, setInterviewerGender] = useState('Male');
  const [difficulty, setDifficulty] = useState('Medium');
  const [interviewType, setInterviewType] = useState('Technical');
  const [duration, setDuration] = useState(10);
  const [numQuestions, setNumQuestions] = useState(5);
  const [previewQuestions, setPreviewQuestions] = useState(false);
  const [randomizeQuestions, setRandomizeQuestions] = useState(false);

  const [generatedInterview, setGeneratedInterview] = useState<GeneratedInterview | null>(null);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleClose = () => {
    setStep(1);
    setJobTitle('');
    setCompanyName('');
    setJobDescription('');
    setSeniority('Mid');
    setInterviewerGender('Male');
    setDifficulty('Medium');
    setInterviewType('Technical');
    setDuration(10);
    setNumQuestions(5);
    setPreviewQuestions(false);
    setRandomizeQuestions(false);
    setGeneratedInterview(null);
    setError('');
    onClose();
  };

  const handleNext = () => {
    if (step === 1 && jobTitle.trim()) {
      if (isRecruiterValue && (!companyName.trim() || !jobDescription.trim())) {
        setError('Please fill in company name and job description');
        return;
      }
      setError('');
      setStep(2);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else if (step === 3) {
      setStep(2);
    }
  };

  const handleGenerate = async () => {
    setError('');
    setStep('loading');

    try {
      console.log('[MODAL] Starting interview generation...');
      const companyNameToUse = isRecruiterValue ? companyName.trim() : 'Practice Company';
      const jobDescToUse = isRecruiterValue ? jobDescription.trim() : `${seniority} level ${interviewType} interview`;

      const requestBody = {
        jobTitle: jobTitle.trim(),
        companyName: companyNameToUse,
        jobDescription: jobDescToUse,
        interviewType,
        difficultyLevel: difficulty,
        duration: duration,
        numQuestions: numQuestions,
        voiceGender: interviewerGender.toLowerCase(),
        previewQuestions: previewQuestions,
        randomizeQuestions: randomizeQuestions,
        organizationId: isRecruiterValue ? organizationId : undefined,
      };
      console.log('[MODAL] Request body:', requestBody);

      const { edgeFunctions } = await import('@/lib/edge-functions');
      const data = await edgeFunctions.createCustomInterview(requestBody);
      console.log('[MODAL] Response data:', data);

      if (isRecruiterValue) {
        data.createdJobId = data.jobId;
      }

      setGeneratedInterview(data);
      setStep(3);
    } catch (err: any) {
      console.error('[MODAL] Error:', err);
      if (err.name === 'AbortError') {
        setError('Request timed out. Please try again.');
      } else {
        setError(err.message || 'Connection error. Please check your network and try again.');
      }
      setStep(2);
    }
  };

  const handleStartInterview = () => {
    if (generatedInterview) {
      if (isRecruiterValue && generatedInterview.createdJobId) {
        handleClose();
        navigate(`/jobs/${generatedInterview.createdJobId}`);
      } else {
        const jobData = {
          id: generatedInterview.jobId,
          title: jobTitle,
          company_name: isRecruiterValue ? companyName : 'Practice Company',
          description: isRecruiterValue ? jobDescription : `${seniority} level ${interviewType} interview`,
          voiceGender: interviewerGender.toLowerCase(),
          interview_configurations: {
            max_duration_minutes: duration,
            voice_gender: interviewerGender.toLowerCase(),
          }
        };
        sessionStorage.setItem('interviewJobInfo', JSON.stringify(jobData));

        handleClose();
        navigate(`/interview?jobId=${generatedInterview.jobId}`);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-scale-in">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-2xl font-bold text-gray-900">
            {isRecruiterValue ? 'Quick Create Job' : 'Create a Custom Interview'}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {step === 1 && (
            <div className="p-6 space-y-6 animate-fade-in">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <p className="text-gray-600">
                {isRecruiterValue
                  ? 'Quickly create a job posting with a custom interview configuration.'
                  : 'Use this for unique roles or jobs not currently listed in the app.'}
              </p>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="jobTitle" className="text-sm font-medium text-gray-700 mb-2 block">
                    Job title <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="jobTitle"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="Salesforce Developer"
                    className="text-base"
                  />
                </div>

                {isRecruiterValue && (
                  <>
                    <div>
                      <Label htmlFor="companyName" className="text-sm font-medium text-gray-700 mb-2 block">
                        Company name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="companyName"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="TechCorp Inc."
                        className="text-base"
                      />
                    </div>

                    <div>
                      <Label htmlFor="jobDescription" className="text-sm font-medium text-gray-700 mb-2 block">
                        Job description <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="jobDescription"
                        value={jobDescription}
                        onChange={(e) => setJobDescription(e.target.value)}
                        placeholder="Describe the role and responsibilities..."
                        rows={3}
                        className="text-base"
                      />
                    </div>
                  </>
                )}

                <div>
                  <Label htmlFor="seniority" className="text-sm font-medium text-gray-700 mb-2 block">
                    Seniority
                  </Label>
                  <select
                    id="seniority"
                    value={seniority}
                    onChange={(e) => setSeniority(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="Junior">Junior</option>
                    <option value="Mid">Mid</option>
                    <option value="Senior">Senior</option>
                    <option value="Lead">Lead</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="p-6 space-y-6 animate-fade-in">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-3 block">
                    Interviewer voice
                  </Label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setInterviewerGender('Male')}
                      className={`flex-1 p-4 border-2 rounded-lg transition-all ${
                        interviewerGender === 'Male'
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-gray-900">Male</div>
                    </button>
                    <button
                      onClick={() => setInterviewerGender('Female')}
                      className={`flex-1 p-4 border-2 rounded-lg transition-all ${
                        interviewerGender === 'Female'
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-gray-900">Female</div>
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-3 block">
                    Interview type
                  </Label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setInterviewType('Technical')}
                      className={`flex-1 p-4 border-2 rounded-lg transition-all ${
                        interviewType === 'Technical'
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-gray-900">Technical</div>
                      <div className="text-xs text-gray-600 mt-1">Skills & knowledge-based</div>
                    </button>
                    <button
                      onClick={() => setInterviewType('Behavioral')}
                      className={`flex-1 p-4 border-2 rounded-lg transition-all ${
                        interviewType === 'Behavioral'
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-gray-900">Behavioral</div>
                      <div className="text-xs text-gray-600 mt-1">Situational & teamwork</div>
                    </button>
                    <button
                      onClick={() => setInterviewType('Mixed')}
                      className={`flex-1 p-4 border-2 rounded-lg transition-all ${
                        interviewType === 'Mixed'
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-gray-900">Mixed</div>
                      <div className="text-xs text-gray-600 mt-1">Both types</div>
                    </button>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-3 block">
                    Difficulty
                  </Label>
                  <div className="flex gap-3">
                    {['Easy', 'Medium', 'Hard'].map((level) => (
                      <button
                        key={level}
                        onClick={() => setDifficulty(level)}
                        className={`flex-1 p-4 border-2 rounded-lg transition-all ${
                          difficulty === level
                            ? 'border-green-600 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-medium text-gray-900">{level}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="duration" className="text-sm font-medium text-gray-700 mb-2 block">
                      Duration (minutes)
                    </Label>
                    <Input
                      id="duration"
                      type="number"
                      min="1"
                      max="10"
                      value={duration}
                      onChange={(e) => setDuration(Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))}
                      className="text-base"
                    />
                    <p className="text-xs text-gray-500 mt-1">Max 10 minutes</p>
                  </div>

                  <div>
                    <Label htmlFor="numQuestions" className="text-sm font-medium text-gray-700 mb-2 block">
                      Number of questions
                    </Label>
                    <Input
                      id="numQuestions"
                      type="number"
                      min="1"
                      max="10"
                      value={numQuestions}
                      onChange={(e) => setNumQuestions(Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))}
                      className="text-base"
                    />
                    <p className="text-xs text-gray-500 mt-1">Max 10 questions</p>
                  </div>
                </div>

                <div className="pt-2 space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={previewQuestions}
                      onChange={(e) => setPreviewQuestions(e.target.checked)}
                      className="mt-1 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700">
                      Preview questions before starting
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={randomizeQuestions}
                      onChange={(e) => setRandomizeQuestions(e.target.checked)}
                      className="mt-1 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700">
                      Randomize question order
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {step === 'loading' && (
            <div className="p-12 flex flex-col items-center justify-center space-y-6 animate-fade-in">
              <div className="relative">
                <Loader2 className="w-16 h-16 text-green-600 animate-spin" />
                <Sparkles className="w-8 h-8 text-green-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Designing your interview...
                </h3>
                <p className="text-gray-600">This usually takes a moment.</p>
              </div>
            </div>
          )}

          {step === 3 && generatedInterview && (
            <div className="p-6 space-y-6 animate-fade-in">
              <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900">Your Custom Interview Is Ready</p>
                  <p className="text-sm text-green-700 mt-1">
                    Based on your job title and difficulty, we've created a realistic interview for this role.
                  </p>
                </div>
              </div>

              <Card className="p-4 bg-gray-50 border-gray-200">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Job title:</span>
                    <div className="font-medium text-gray-900">{generatedInterview.jobTitle}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Seniority:</span>
                    <div className="font-medium text-gray-900">{generatedInterview.seniority}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Difficulty:</span>
                    <div className="font-medium text-gray-900">{generatedInterview.difficulty}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Interviewer:</span>
                    <div className="font-medium text-gray-900">{generatedInterview.interviewerGender}</div>
                  </div>
                </div>
              </Card>

              <Card className="p-4 bg-blue-50 border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-3">Tips for this interview</h4>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 flex-shrink-0"></div>
                    <span>Speak clearly and structure your answers.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 flex-shrink-0"></div>
                    <span>Use real examples from your experience.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 flex-shrink-0"></div>
                    <span>Keep eye contact with the camera where possible.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-600 mt-1.5 flex-shrink-0"></div>
                    <span>Take a breath before answering difficult questions.</span>
                  </li>
                </ul>
              </Card>

              {generatedInterview.previewQuestions ? (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Interview Questions</h4>
                  <div className="space-y-3">
                    {generatedInterview.questions.map((q, idx) => (
                      <Card key={q.id} className="p-4 border-gray-200">
                        <div className="flex gap-3">
                          <div className="w-8 h-8 rounded-full bg-green-100 text-green-700 font-semibold flex items-center justify-center flex-shrink-0">
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <p className="text-gray-900">{q.question}</p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              ) : (
                <Card className="p-4 bg-gray-50 border-gray-200">
                  <p className="text-sm text-gray-700 italic">
                    You chose not to preview the questions. Your interviewer will ask them one by one, just like a real interview.
                  </p>
                </Card>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            {(step === 1 || step === 2 || step === 3) && (
              <button
                onClick={step === 3 ? handleBack : handleClose}
                className="text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors"
              >
                {step === 3 ? <div className="flex items-center gap-2"><ArrowLeft className="w-4 h-4" />Back</div> : 'Cancel'}
              </button>
            )}
          </div>
          <div>
            {step === 1 && (
              <Button
                onClick={handleNext}
                disabled={!jobTitle.trim()}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                Next
              </Button>
            )}
            {step === 2 && (
              <div className="flex gap-2">
                <Button
                  onClick={handleBack}
                  variant="outline"
                  className="border-gray-300"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleGenerate}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {isRecruiterValue ? 'Create Job' : 'Generate Interview'}
                </Button>
              </div>
            )}
            {step === 3 && (
              <Button
                onClick={handleStartInterview}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {isRecruiterValue ? 'View Job' : 'Start Interview'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
